import { Avatar, Checkbox, Tooltip } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { getTabTypeBySessionType, renderClubsCell } from "@/lib/accountsUtils";
import { getEnumNumberValues, mapExploitationStageToStringName } from "@/lib/common-utils";
import { AccountTypeEnum, APP_GMT_DATE_FORMAT, ExploitationStage } from "@/lib/constants";
import { SysAccountSessionRegularDto } from "@/shared/api";
import { AnyObj, NilObj } from "@/types/commonTypes";
import { osType } from "@/components/config-modals/router-edit-modal/constants";

const useDebugSessionsColumns = ({ roomName }: { roomName: string }) => {
  const { t } = useTranslation();

  return useMemo<MRT_ColumnDef<SysAccountSessionRegularDto>[]>(
    () => [
      // === Старые колонки ===
      {
        id: "$.avatar",
        header: t("sessionColumns.avatar"),
        columnFilterModeOptions: ["contains", "empty", "notEmpty"],
        accessorFn: (row) => row.avatar ?? "-",
        Cell: ({ row }) => <Avatar alt={row.original.nick ?? "?"} src={row.original.avatar} />,
      },
      {
        id: "$.username",
        header: t("sessionColumns.username"),
        accessorFn: (row) => row.username ?? "-",
      },
      {
        id: "$.pid",
        header: t("sessionColumns.pid"),
        accessorFn: (row) => row.pid ?? "-",
      },
      {
        id: "$.nick",
        header: t("sessionColumns.nick"),
        accessorFn: (row) => row.nick ?? "-",
      },
      {
        id: "$.account.data.clubs",
        header: t("sessionColumns.clubs"),
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row: { original } }) => renderClubsCell(original.account?.data),
      },
      {
        id: "$.cid",
        header: t("sessionColumns.cid"),
        accessorFn: (row) => row.cid ?? "-",
      },
      {
        id: "$.aid",
        header: t("sessionColumns.aid"),
        accessorFn: (row) => row.aid ?? "-",
      },
      {
        id: "$.game_info.tid",
        header: t("sessionColumns.tid"),
        accessorFn: (row) => row.game_info?.tid ?? "-",
      },
      {
        id: "$.account.data.alc",
        header: t("sessionColumns.alc"),
        accessorFn: ({ account }) => (account?.data as NilObj)?.alc ?? "-",
      },
      {
        id: "$.account.data.game_group",
        header: t("Game group"),
        accessorFn: ({ account }) => (account?.data as NilObj)?.game_group ?? "-",
      },
      {
        id: "$.account.data.account_type",
        header: t("serviceAccountsTable.accountType"),
        order: 301,
        accessorFn: ({ account }) => {
          const accountType = (account as AnyObj)?.data.account_type;
          if (typeof accountType !== "number") return "-";
          return `${accountType} (${AccountTypeEnum[accountType]})`;
        },
      },
      {
        id: "$.gold",
        header: t("sessionColumns.gold"),
        accessorFn: (row) => row.gold ?? "-",
      },
      {
        id: "$.game_info.name",
        header: t("sessionColumns.tableName"),
        accessorFn: (row) => row.game_info?.name ?? "-",
      },
      {
        id: "$.game_info.type",
        header: t("sessionColumns.gameType"),
        accessorFn: (row) => row.game_info?.type ?? "-",
      },
      {
        id: "$.game_info.limit",
        header: t("sessionColumns.tableLimit"),
        accessorFn: (row) => row.game_info?.limit ?? "-",
      },
      {
        id: "$.game_info.hands",
        header: t("sessionColumns.tableHands"),
        accessorFn: (row) => row.game_info?.hands ?? "-",
      },
      {
        id: "$.game_info.result",
        header: t("sessionColumns.tableResult"),
        accessorFn: (row) => row.game_info?.result ?? "-",
      },
      {
        id: "$.current_stats.vpip",
        header: t("sessionColumns.vpip"),
        accessorFn: (row) => row.current_stats?.vpip ?? "-",
      },
      {
        id: "$.current_stats.pfr",
        header: t("sessionColumns.pfr"),
        accessorFn: (row) => row.current_stats?.pfr ?? "-",
      },
      {
        id: "$.current_stats.hands",
        header: t("sessionColumns.hands"),
        accessorFn: (row) => row.current_stats?.hands ?? "-",
      },
      {
        id: "$.proxy",
        header: t("sessionColumns.proxy"),
        accessorFn: (row) => row.proxy ?? "-",
      },
      {
        id: "$.schedule",
        header: t("sessionColumns.schedule"),
        accessorFn: (row) => row.schedule ?? "-",
      },
      {
        id: "$.gs_instance.id",
        header: t("sessionColumns.gs"),
        accessorFn: (row) => row.gs_instance?.id ?? "-",
      },
      {
        id: "$.gs_instance.version",
        header: t("sessionColumns.gsVersion"),
        accessorFn: (row) => row.gs_instance?.version ?? "-",
      },
      {
        id: "$.gs_instance.mode",
        header: t("sessionColumns.gsMode"),
        accessorFn: (row) =>
          getEnumNumberValues(ExploitationStage)
            .filter((item) => item === row.gs_instance?.mode)
            .map((item) => ({
              label: mapExploitationStageToStringName(item),
              value: item,
            }))
            .at(0)?.label ?? "-",
      },
      {
        id: "$.tes_instance.id",
        header: t("sessionColumns.tes"),
        accessorFn: (row) => row.tes_instance?.id ?? "-",
      },
      {
        id: "$.tes_instance.version",
        header: t("sessionColumns.tesVersion"),
        accessorFn: (row) => row.tes_instance?.version ?? "-",
      },
      {
        id: "$.tes_instance.mode",
        header: t("sessionColumns.tesMode"),
        accessorFn: (row) =>
          getEnumNumberValues(ExploitationStage)
            .filter((item) => item === row.tes_instance?.mode)
            .map((item) => ({
              label: mapExploitationStageToStringName(item),
              value: item,
            }))
            .at(0)?.label ?? "-",
      },

      // === Новые колонки из примеров (уникальные) ===
      {
        id: "account.data.avatar",
        header: t("commonColumns.avatar"),
        columnFilterModeOptions: ["contains", "empty", "notEmpty"],
        order: 100,
        accessorFn: ({ account }) => (account?.data as any)?.avatar ?? "",
        Cell: ({ row }) => {
          const dt = row.original.account?.data as any;
          return <Avatar alt={dt?.nick ?? "?"} src={dt?.avatar} />;
        },
      },
      {
        id: "account.name",
        header: t("commonColumns.name"),
        order: 200,
        accessorFn: ({ account }) => account?.name ?? "-",
      },
      {
        id: "account.data.source",
        header: t("commonColumns.source"),
        order: 300,
        accessorFn: ({ account }) => (account?.data as NilObj)?.source ?? "-",
      },
      {
        id: "account.data.gems",
        header: t("regularAccountsTable.gems"),
        order: 601,
        accessorFn: ({ account }) => (account?.data as NilObj)?.gems ?? "-",
      },
      {
        id: "account.data.bound_gems",
        header: t("regularAccountsTable.boundGems"),
        order: 601,
        accessorFn: ({ account }) => (account?.data as NilObj)?.bound_gems ?? "-",
      },
      {
        id: "account.data.os_type",
        header: t("osType"),
        order: 600,
        accessorFn: ({ account }) =>
          osType.map((i) => i.label)[(account?.data as NilObj)?.device?.os_type as unknown as number] ?? "-",
      },
      {
        id: "account.data.status",
        header: t("commonColumns.status"),
        order: 700,
        accessorFn: ({ account }) => (account?.data as NilObj)?.status ?? "-",
        Cell: ({ row: { original } }) => {
          const status = (original.account?.data as NilObj)?.status as unknown;
          return status !== undefined && typeof status === "number" ? (
            <Tooltip title={t(`accountStatus.${status}`)}><p>{status}</p></Tooltip>
          ) : (
            "-"
          );
        },
      },
      {
        id: "account.data.comment",
        header: t("serviceAccountsTable.comment"),
        order: 301,
        accessorFn: ({ account }) => (account?.data as NilObj)?.comment ?? "-",
      },
      {
        id: "account.created_at",
        header: t("commonColumns.createdAt"),
        order: 800,
        accessorFn: ({ account }) => (account?.created_at ? new Date(account.created_at) : 0),
        Cell: ({ row: { original } }) =>
          original.account?.created_at
            ? moment(original.account.created_at).utc(false).format(APP_GMT_DATE_FORMAT)
            : "-",
      },
      {
        id: "account.enabled",
        header: t("commonColumns.enabled"),
        order: 900,
        accessorFn: ({ account }) => account?.enabled ?? "-",
        Cell: ({ row: { original } }) => <Checkbox checked={original.account?.enabled} disabled />,
      },
      {
        id: "account.session.id",
        header: t("commonColumns.session"),
        order: 1000,
        accessorFn: ({ account }) => ((account?.data as any)?.session?.id ? 1 : 0),
        Cell: ({ row: { original } }) =>
          (original.account?.data as any)?.session ? (
            <Link
              to="/accounts/$room/$type"
              params={{
                room: roomName,
                type: getTabTypeBySessionType((original.account?.data as any)?.session?.session_type),
              }}
              search={{
                session_id: (original.account?.data as any)?.session?.id,
              }}
            >
              Active
            </Link>
          ) : (
            "-"
          ),
      },
      {
        id: "account.data.game_type",
        header: t("regularAccountsTable.gameType"),
        order: 601,
        accessorFn: ({ account }) => (account?.data as NilObj)?.game_type ?? "-",
      },
      {
        id: "account.data.external_id",
        header: t("regularAccountsTable.external_id"),
        order: 601,
        accessorFn: ({ account }) => (account?.data as NilObj)?.external_id ?? "-",
      },
      {
        id: "account.data.behaviour",
        header: t("regularAccountsTable.behaviour"),
        order: 601,
        accessorFn: ({ account }) => (account?.data as NilObj)?.behaviour ?? "-",
      },
      {
        id: "account.data.ai_profile",
        header: t("regularAccountsTable.aiProfile"),
        order: 601,
        accessorFn: ({ account }) => (account?.data as NilObj)?.ai_profile ?? "-",
      },
      {
        id: "account.data.ai_profile_ob",
        header: t("regularAccountsTable.aiProfileOb"),
        order: 601,
        accessorFn: ({ account }) => (account?.data as NilObj)?.ai_profile_ob ?? "-",
      },
      {
        id: "account.data.timing_profile",
        header: t("regularAccountsTable.timing"),
        order: 601,
        accessorFn: ({ account }) => (account?.data as NilObj)?.timing_profile ?? "-",
      },
      {
        id: "account.data.timing_profile_ob",
        header: t("regularAccountsTable.timingOb"),
        order: 601,
        accessorFn: ({ account }) => (account?.data as NilObj)?.timing_profile_ob ?? "-",
      },
      {
        id: "account.data.insurance_profile",
        header: t("regularAccountsTable.insurance"),
        order: 601,
        accessorFn: ({ account }) => (account?.data as NilObj)?.insurance_profile ?? "-",
      },
      {
        id: "account.data.insurance_profile_ob",
        header: t("regularAccountsTable.insuranceOb"),
        order: 601,
        accessorFn: ({ account }) => (account?.data as NilObj)?.insurance_profile_ob ?? "-",
      },
    ],
    [t, roomName]
  );
};

export default useDebugSessionsColumns;
